import { Box, HStack, Stack, Icon, Text, Hide } from '@chakra-ui/react';
import { NestedHome } from '@habity/types';
import { ICONS } from '~/constants/icons';
import { ListSkeleton } from '../../../homes/list/components/ListSkeleton';
import {
  getPrice,
  formatHomeData,
  getStreet,
  getCategoryText,
  getNextViewing,
  formatNextViewing,
  getLocationDisplayText,
  isSold,
  shouldShowPrice,
} from '@habity/services';
import { useTranslations } from 'next-intl';
import { hasPrice } from '~/lib/homes';
import { Link } from '@chakra-ui/next-js';

interface HomeCardDataProps {
  home: NestedHome;
  isLoading?: boolean;
  href?: string;
  onClick?: () => void;
  variant?: 'default' | 'compact' | 'display';
  showBottomBorder?: boolean;
}

export const HomeCardData = ({
  home,
  isLoading = false,
  href,
  onClick,
  variant = 'default',
  showBottomBorder = true,
}: HomeCardDataProps) => {
  const t = useTranslations('Home');
  const { address, status, is_bidding_active, viewings } = home || {};
  const { category } = address || {};
  const { show_verified_badge } = status || {};
  const show_price = shouldShowPrice(home);

  const locationName = getLocationDisplayText(address) || t('noCityAvailable');
  const nextViewing = getNextViewing(viewings);

  const content = (
    <Stack
      spacing="1"
      pt={0}
      pb={variant === 'display' ? 10 : 3}
      cursor="pointer"
      justifyContent="flex-start"
      borderBottomWidth={
        showBottomBorder ? (variant === 'compact' ? '0' : '1px') : '0'
      }
      borderBottomColor="blackAlpha.500"
      onClick={onClick}
    >
      {/* Street */}
      <Box
        mt="1" // To make room for diacritics
        noOfLines={1}
      >
        <HStack
          spacing="1"
          justifyContent="flex-start"
          h="1.5rem" // Preserve the space for grid alignment
        >
          <Text
            size="md"
            fontWeight="medium"
            flexShrink="0"
            noOfLines={1}
            _groupHover={{ textDecoration: 'underline' }}
          >
            {getStreet(home)}
          </Text>

          {show_verified_badge && <Icon as={ICONS.CLAIMED} color="black" />}
        </HStack>
      </Box>

      {/* Category */}
      <HStack
        justifyContent="flex-start"
        h="1rem" // Preserve the space for grid alignment
      >
        <Text
          color="blackAlpha.700"
          size="sm"
          lineHeight={1.2} // Needed to make room for diacritics
          noOfLines={1}
        >
          {`${getCategoryText(category)}, ${locationName}`}
        </Text>
      </HStack>

      {variant !== 'display' && (
        <>
          {/* Features */}
          <HStack
            alignItems="baseline"
            justifyContent="flex-start"
            h="1.2rem" // Preserve the space for grid alignment
          >
            <Text size={variant === 'compact' ? 'xs' : 'sm'} noOfLines={1}>
              {formatHomeData(home)}
            </Text>
          </HStack>

          {/* Price */}
          <Box
            mt="0"
            display="flex"
            alignItems="baseline"
            h="1.5rem" // Preserve the space for grid alignment
          >
            {show_price && (
              <HStack spacing="2">
                <Text size="sm">
                  {status?.name === 'sold' ? `${t('Price.sales-price')} ` : ''}
                  {hasPrice(home) ? getPrice(home) : t('Price.no-price')}
                </Text>
              </HStack>
            )}
          </Box>

          {/* Bidding and viewing status */}
          <HStack
            mt="3"
            h="1rem" // Preserve the space for grid alignment
            spacing="2"
            justifyContent="space-between"
          >
            {!isSold(home) && is_bidding_active && (
              <HStack spacing="1">
                <Box
                  boxSize="8px"
                  rounded="full"
                  backgroundColor="burntOrange"
                />
                <Text size="sm" color="blackAlpha.700" fontWeight="light">
                  {t('PriceDescription.bidding-active')}
                </Text>
              </HStack>
            )}
            {!isSold(home) && nextViewing && (
              <Text size="sm" color="blackAlpha.700" fontWeight="light">
                <Hide below="desktop">
                  <span>Nästa visning: </span>
                </Hide>

                <span>{formatNextViewing(nextViewing)}</span>
              </Text>
            )}
          </HStack>
        </>
      )}
    </Stack>
  );

  // Skeleton state which matches content layout
  if (isLoading) {
    return (
      <Stack
        spacing="2.5"
        pt={1}
        pb={variant === 'display' ? 10 : 3}
        cursor="pointer"
        justifyContent="flex-start"
        borderBottomWidth="1px"
        borderBottomColor="blackAlpha.500"
      >
        {/* Street */}
        <ListSkeleton isLoaded={false} h="20px" w="350px" />

        {/* Category */}
        <ListSkeleton isLoaded={false} h="14px" w="200px" />

        {variant !== 'display' && (
          <>
            {/* Features */}
            <ListSkeleton isLoaded={false} h="14px" w="250px" />

            {/* Price */}
            <ListSkeleton isLoaded={false} h="14px" w="100px" />

            {/* Bidding */}
            <ListSkeleton isLoaded={false} h="14px" w="200px" mt="14px" />
          </>
        )}
      </Stack>
    );
  }

  if (!href) return content;

  return (
    <Link href={href} _hover={{ textDecoration: 'none' }}>
      {content}
    </Link>
  );
};
